import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Checkbox,
  alpha,
  useTheme,
  Pagination,
  Snackbar,
  Divider,
  Stack,
  Badge,
  TableSortLabel,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Verified as VerifiedIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
  MiscellaneousServices as ServiceIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { passportService } from '../services/passportService';
import StatusChip from '../components/common/StatusChip';
import { PassportRecord, ApplicationStatus, ServiceName } from '../types';

const ManageRecords: React.FC = () => {
  const theme = useTheme();
  const { user, hasRole, hasPermission } = useAuth();
  const [records, setRecords] = useState<PassportRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  const [searchParams, setSearchParams] = useState({
    passport_no: '',
    mobile_number: '',
    enrolment_no: '',
    money_receipt_no: '',
    iqama_number: '',
    employer_name: '',
    place_area: '',
    from_date: '',
    to_date: '',
    status: '',
    passport_type: '',
    service_code: '',
  });
  
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  
  // Selection state
  const [selectedRecords, setSelectedRecords] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Dialog states
  const [selectedRecord, setSelectedRecord] = useState<PassportRecord | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [bulkVerifyDialogOpen, setBulkVerifyDialogOpen] = useState(false);
  
  // Form state for editing
  const [editFormData, setEditFormData] = useState<Partial<PassportRecord>>({});
  const [verifyRemarks, setVerifyRemarks] = useState('');
  const [deleteRemarks, setDeleteRemarks] = useState('');
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});

  // Service names – used by the search filter dropdown
  const [serviceNames, setServiceNames] = useState<ServiceName[]>([]);
  const [serviceNamesLoading, setServiceNamesLoading] = useState(false);

  useEffect(() => {
    fetchRecords();
  }, [page]);

  // Fetch service names once on mount for the filter dropdown
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setServiceNamesLoading(true);
      try {
        const data = await passportService.getServiceNames();
        if (!cancelled) setServiceNames(data);
      } catch {
        // non-fatal – dropdown will be empty
      } finally {
        if (!cancelled) setServiceNamesLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        ...searchParams,
        page,
        limit: 10,
      };
      
      let response;
      if (hasRole('DATA_ENTRY_OFFICER')) {
        response = await passportService.getMyRecords(params as any);
      } else {
        response = await passportService.getAllRecords(params as any);
      }
      
      setRecords(response.results || []);
      setTotalRecords(response.count || 0);
      setTotalPages(Math.ceil((response.count || 0) / 10));
      setSelectedRecords([]);
      setSelectAll(false);
    } catch (err: any) {
      console.error('Fetch error:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch records';
      
      // Handle "Invalid page" error by resetting to page 1
      if (errorMessage.includes('Invalid page')) {
        if (page !== 1) {
          setPage(1);
          setError('Page not found. Returning to first page.');
          // The useEffect will trigger fetchRecords again when page changes to 1
          return;
        }
      }
      
      setError(errorMessage);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const isEditIqamaDatePast = editFormData.expiry_date_of_iqama ? new Date(editFormData.expiry_date_of_iqama) < new Date(new Date().setHours(0, 0, 0, 0)) : false;
  // Calculate serial number based on current page
  const getSerialNumber = (index: number) => {
    return (page - 1) * 10 + index + 1;
  };

  const handleSearch = () => {
    setPage(1);
    fetchRecords();
  };

  const handleReset = () => {
    setSearchParams({
      passport_no: '',
      mobile_number: '',
      enrolment_no: '',
      money_receipt_no: '',
      iqama_number: '',
      employer_name: '',
      place_area: '',
      from_date: '',
      to_date: '',
      status: '',
      passport_type: '',
      service_code: '',
    });
    setPage(1);
    setTimeout(() => fetchRecords(), 0);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRecords([]);
    } else {
      const pendingIds = records
        .filter(r => r.current_status === 'PENDING')
        .map(r => r.passport_applicants_id);
      setSelectedRecords(pendingIds);
    }
    setSelectAll(!selectAll);
  };

  const handleSelectRecord = (id: number) => {
    setSelectedRecords(prev => {
      if (prev.includes(id)) {
        return prev.filter(recordId => recordId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  // View Record
  const handleView = async (record: PassportRecord) => {
    try {
      setLoading(true);
      const fullRecord = await passportService.getRecordById(record.passport_applicants_id);
      setSelectedRecord(fullRecord);
      setViewDialogOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load record details');
    } finally {
      setLoading(false);
    }
  };

  // Edit Record
  const handleEdit = async (record: PassportRecord) => {
    try {
      setLoading(true);
      const fullRecord = await passportService.getRecordById(record.passport_applicants_id);
      setSelectedRecord(fullRecord);
      setEditErrors({});
      setEditFormData({
        applicants_name: fullRecord.applicants_name,
        applicants_profession: fullRecord.applicants_profession,
        applicants_type: fullRecord.applicants_type,
        passport_type: fullRecord.passport_type,
        passport_no: fullRecord.passport_no,
        date_of_entry: convertToInputDate(fullRecord.date_of_entry),
        iqama_number: fullRecord.iqama_number || '',
        mobile_number: fullRecord.mobile_number,
        expiry_date_of_iqama: convertToInputDate(fullRecord.expiry_date_of_iqama || ''),
        employer_name: fullRecord.employer_name || '',
        place_area: fullRecord.place_area || '',
        enrolment_no: fullRecord.enrolment_no,
        money_receipt_no: fullRecord.money_receipt_no || '',
        amount_jd: fullRecord.amount_jd,
        service_code: fullRecord.service_code,
        remarks: fullRecord.remarks || '',
      });
      setEditDialogOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load record for editing');
    } finally {
      setLoading(false);
    }
  };

  // --- inline validation for edit modal (mirrors DataEntryForm rules) ---
  const validateEditForm = (): Record<string, string> => {
    const errs: Record<string, string> = {};
    const d = editFormData;

    // applicants_name
    if (!d.applicants_name || d.applicants_name.trim().length < 2)
      errs.applicants_name = 'Name must be at least 2 characters';
    
    // applicants_profession
    if (!d.applicants_profession || d.applicants_profession.trim().length < 2)
      errs.applicants_profession = 'Profession must be at least 2 characters';

    // applicants_type
    if (!d.applicants_type)
      errs.applicants_type = 'Applicant type is required';

    // passport_type
    if (!d.passport_type)
      errs.passport_type = 'Passport type is required';

    // passport_no
    if (!d.passport_no || d.passport_no.trim().length < 6)
      errs.passport_no = 'Passport number must be at least 6 characters';

    // date_of_entry – must not be in the future
    if (!d.date_of_entry) {
      errs.date_of_entry = 'Date of entry is required';
    } else {
      const today = new Date().toISOString().split('T')[0];
      if (d.date_of_entry > today)
        errs.date_of_entry = 'Date of entry cannot be in the future';
    }

    // mobile_number – Jordanian format 07xxxxxxxx
    if (!d.mobile_number) {
      errs.mobile_number = 'Mobile number is required';
    } else {
      const cleaned = d.mobile_number.replace(/\D/g, '');
      if (!/^07\d{8}$/.test(cleaned))
        errs.mobile_number = 'Enter a valid Jordanian mobile number (e.g. 07XXXXXXXX)';
    }

    // place_area
    if (!d.place_area || d.place_area.trim().length === 0)
      errs.place_area = 'Place/Area is required';

    // enrolment_no
    if (!d.enrolment_no) {
      errs.enrolment_no = 'Enrolment number is required';
    } else if (!/^MRP\d{14}$/.test(d.enrolment_no) && d.enrolment_no.trim().length < 5) {
      errs.enrolment_no = 'Enrolment number must be at least 5 characters, or follow the MRP auto-generated format (MRPDDMMYYYYHHmmss)';
    }

    // employer_name: mandatory when type is ORGANIZATION
    if (d.applicants_type === 'ORGANIZATION' && (!d.employer_name || d.employer_name.trim().length === 0))
      errs.employer_name = 'Employer name is required for Organization type';

    // iqama conditional: if iqama_number given, expiry is required & must be future
    if (d.iqama_number && d.iqama_number.trim() !== '') {
      if (!d.expiry_date_of_iqama || d.expiry_date_of_iqama.trim() === '') {
        errs.expiry_date_of_iqama = 'Expiry date is required when iqama number is provided';
      } 
      /*else {
        const today = new Date(); today.setHours(0,0,0,0);
        if (new Date(d.expiry_date_of_iqama) <= today)
          errs.expiry_date_of_iqama = 'Expiry date must be in the future';
      }*/
    }

    // service_code
    if (!d.service_code)
      errs.service_code = 'Service is required';

    // amount_jd – if present, must be positive
    if (d.amount_jd !== undefined && d.amount_jd !== null) {
      const amt = Number(d.amount_jd);
      if (isNaN(amt) || amt < 0)
        errs.amount_jd = 'Amount must be a positive number';
      if (amt > 9999999.99)
        errs.amount_jd = 'Amount is too large';
    }

    return errs;
  };

  // helper: update a single field and clear its error immediately
  const handleEditChange = (field: string, value: any) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
    // clear the error for this field as the user types
    if (editErrors[field]) {
      setEditErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
    }
    // special: if iqama_number is cleared, also clear expiry and its error
    if (field === 'iqama_number' && (!value || value.trim() === '')) {
      setEditFormData(prev => ({ ...prev, expiry_date_of_iqama: '' }));
      setEditErrors(prev => { const n = { ...prev }; delete n.expiry_date_of_iqama; return n; });
    }
    // special: INDIVIDUAL → auto-set employer_name to "Self"
    if (field === 'applicants_type') {
      if (value === 'INDIVIDUAL') {
        setEditFormData(prev => ({ ...prev, employer_name: 'Self' }));
      } else if (editFormData.employer_name === 'Self') {
        setEditFormData(prev => ({ ...prev, employer_name: '' }));
      }
    }
    // special: passport_type changed → mirror DataEntryForm MRP logic
    if (field === 'passport_type') {
      if (value === 'MRP') {
        const now = new Date();
        const pad = (n: number, len = 2) => String(n).padStart(len, '0');
        const mrp =
          'MRP' +
          pad(now.getDate()) +
          pad(now.getMonth() + 1) +
          now.getFullYear() +
          pad(now.getHours()) +
          pad(now.getMinutes()) +
          pad(now.getSeconds());
        setEditFormData(prev => ({ ...prev, enrolment_no: mrp }));
        setEditErrors(prev => { const n = { ...prev }; delete n.enrolment_no; return n; });
      } else {
        // Clear only if the current value is an MRP-generated one
        setEditFormData(prev => {
          if (prev.enrolment_no && prev.enrolment_no.startsWith('MRP')) {
            return { ...prev, enrolment_no: '' };
          }
          return prev;
        });
      }
    }
  };

  const handleUpdateRecord = async () => {
    if (!selectedRecord) return;

    // run client-side validation first
    const errs = validateEditForm();
    if (Object.keys(errs).length > 0) {
      setEditErrors(errs);
      return;          // stop – show inline errors
    }

    try {
      setLoading(true);
      
      // Convert dates to API format (YYYY-MM-DD)
      const dataToSend = {
        ...editFormData,
        date_of_entry: convertToApiDate(editFormData.date_of_entry || ''),
        expiry_date_of_iqama: editFormData.expiry_date_of_iqama 
          ? convertToApiDate(editFormData.expiry_date_of_iqama) 
          : null,
      };
      
      await passportService.updateRecord(selectedRecord.passport_applicants_id, dataToSend);
      setSuccessMessage('Record updated successfully!');
      setEditDialogOpen(false);
      fetchRecords();
    } catch (err: any) {
      // api.ts handleError() rejects with: { status, message, data: <DRF body> }
      // DRF field errors live in err.data; fall back to raw axios shape for safety.
      const responseData: Record<string, any> =
        err?.data ??
        err?.response?.data ??
        {};

      const extract = (v: unknown): string =>
        Array.isArray(v) ? String(v[0]) : String(v ?? '');

      const fieldErrors: Record<string, string> = {};
      const fieldErrorMessages: string[] = [];

      const knownFields = [
        'applicants_name', 'applicants_profession', 'applicants_type', 'passport_type', 'passport_no',
        'date_of_entry', 'iqama_number', 'mobile_number', 'expiry_date_of_iqama',
        'employer_name', 'place_area', 'enrolment_no', 'money_receipt_no',
        'amount_jd', 'service_code', 'remarks',
      ];

      knownFields.forEach((field) => {
        if (responseData[field]) {
          const msg = extract(responseData[field]);
          fieldErrors[field] = msg;
          fieldErrorMessages.push(msg);
        }
      });

      if (Object.keys(fieldErrors).length > 0) {
        // Surface each error next to its input field
        setEditErrors(prev => ({ ...prev, ...fieldErrors }));
      }

      if (responseData.non_field_errors) {
        fieldErrorMessages.push(extract(responseData.non_field_errors));
      }

      setError(
        fieldErrorMessages.length > 0
          ? fieldErrorMessages.join(' ')
          : responseData.detail ||
            responseData.message ||
            err?.message ||
            'Failed to update record. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  // Delete Record
  const handleDelete = (record: PassportRecord) => {
    setSelectedRecord(record);
    setDeleteRemarks('');
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedRecord) return;
    
    try {
      setLoading(true);
      await passportService.deleteRecord(selectedRecord.passport_applicants_id);
      setSuccessMessage('Record deleted successfully!');
      setDeleteDialogOpen(false);
      fetchRecords();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.response?.data?.error || 'Failed to delete record');
    } finally {
      setLoading(false);
    }
  };

  // Verify Single Record
  const handleVerify = (record: PassportRecord) => {
    setSelectedRecord(record);
    setVerifyRemarks('Verified by manager');
    setVerifyDialogOpen(true);
  };

  const handleConfirmVerify = async () => {
    if (!selectedRecord) return;
    
    try {
      setLoading(true);
      await passportService.verifyRecord(selectedRecord.passport_applicants_id, verifyRemarks);
      setSuccessMessage('Record verified successfully!');
      setVerifyDialogOpen(false);
      fetchRecords();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.response?.data?.error || 'Failed to verify record');
    } finally {
      setLoading(false);
    }
  };

  // Bulk Verify Records
  const handleBulkVerify = () => {
    if (selectedRecords.length === 0) {
      setError('Please select at least one record to verify');
      return;
    }
    setVerifyRemarks('Bulk verification by manager');
    setBulkVerifyDialogOpen(true);
  };

  const handleConfirmBulkVerify = async () => {
    try {
      setLoading(true);
      const promises = selectedRecords.map(id => 
        passportService.verifyRecord(id, verifyRemarks)
      );
      await Promise.all(promises);
      setSuccessMessage(`Successfully verified ${selectedRecords.length} record(s)!`);
      setBulkVerifyDialogOpen(false);
      setSelectedRecords([]);
      setSelectAll(false);
      fetchRecords();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to verify some records');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '—';
    // Handle YYYY-MM-DD (API format)
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-');
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return `${parseInt(day)}-${months[parseInt(month) - 1]}-${year}`;
    }
    // Handle DD-MM-YYYY (display format)
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      const [day, month, year] = dateString.split('-');
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return `${parseInt(day)}-${months[parseInt(month) - 1]}-${year}`;
    }
    return dateString;
  };

  // Safe datetime formatter – handles ISO 8601, Django datetime strings, and null/undefined
  const formatDateTime = (value: string | null | undefined): string => {
    if (!value) return '—';
    try {
      // Replace space separator with T so older Safari parses it
      const normalized = value.replace(' ', 'T');
      const d = new Date(normalized);
      if (isNaN(d.getTime())) return value; // return raw string rather than "Invalid Date"
      return d.toLocaleString(undefined, {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false,
      });
    } catch {
      return value;
    }
  };

  // Convert DD-MM-YYYY to YYYY-MM-DD for API
  const convertToApiDate = (dateString: string) => {
    if (!dateString) return '';
    // If already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    // If in DD-MM-YYYY format, convert
    const [day, month, year] = dateString.split('-');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  };

  // Convert YYYY-MM-DD to input field format (YYYY-MM-DD)
  const convertToInputDate = (dateString: string) => {
    if (!dateString) return '';
    // If already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    // If in DD-MM-YYYY format, convert
    const [day, month, year] = dateString.split('-');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  };

  // Handle Export
  const handleExport = async () => {
    try {
      setLoading(true);
      const exportParams = { ...searchParams };
      await passportService.exportRecords(exportParams as any, 'excel');
      setSuccessMessage('Records exported successfully!');
    } catch (err: any) {
      setError(err.response?.data?.detail || err?.message || 'Failed to export records');
    } finally {
      setLoading(false);
    }
  };

  const canEdit = (record: PassportRecord) => {
    if (hasRole('LEAD_MANAGER')) return false;

    if (record.current_status !== 'PENDING') return false;

    if (hasRole('DATA_MANAGER')) return true;
    if (hasRole('DATA_ENTRY_OFFICER')) return record.created_by === user?.users_name;

    return false;
  };

  const canDelete = (record: PassportRecord) => {
    if (hasRole('LEAD_MANAGER')) return false;

    if (record.current_status === 'DELETED') return false;

    // VERIFIED → only DATA_MANAGER
    if (record.current_status === 'VERIFIED') return hasRole('DATA_MANAGER');

    // PENDING
    if (hasRole('DATA_MANAGER')) return true;
    if (hasRole('DATA_ENTRY_OFFICER')) return record.created_by === user?.users_name;

    return false;
  };

  const canVerify = (record: PassportRecord) => {
    
    if (hasRole('LEAD_MANAGER')) return false;

    const status = record.current_status;
    return (hasRole('DATA_MANAGER')) && status === 'PENDING';
  };

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto'}}>
      {/* Header */}
      <Box sx={{ mb: 2, py:1 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0, color: 'primary.main' }}>
          Manage Records
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Search, view, and manage application records
        </Typography>
      </Box>

      {/* Search Filters Card */}
      <Card 
        sx={{ 
          mb: 3,
          boxShadow: theme.shadows[3],
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <FilterIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Search Filters
            </Typography>
          </Box>
          
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Passport Type</InputLabel>
                <Select
                  value={searchParams.passport_type}
                  label="Passport Type"
                  onChange={(e) => setSearchParams({...searchParams, passport_type: e.target.value})}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="MRP">Machine Readable (MRP)</MenuItem>
                  <MenuItem value="EPP">Electronic (EPP)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Passport No"
                value={searchParams.passport_no}
                onChange={(e) => setSearchParams({...searchParams, passport_no: e.target.value})}
                size="small"
                variant="outlined"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Mobile Number"
                value={searchParams.mobile_number}
                onChange={(e) => setSearchParams({...searchParams, mobile_number: e.target.value})}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Enrolment No"
                value={searchParams.enrolment_no}
                onChange={(e) => setSearchParams({...searchParams, enrolment_no: e.target.value})}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Money Receipt No"
                value={searchParams.money_receipt_no}
                onChange={(e) => setSearchParams({...searchParams, money_receipt_no: e.target.value})}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Iqama Number"
                value={searchParams.iqama_number}
                onChange={(e) => setSearchParams({...searchParams, iqama_number: e.target.value})}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Employer Name"
                value={searchParams.employer_name}
                onChange={(e) => setSearchParams({...searchParams, employer_name: e.target.value})}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Place / Area"
                value={searchParams.place_area}
                onChange={(e) => setSearchParams({...searchParams, place_area: e.target.value})}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={searchParams.status}
                  label="Status"
                  onChange={(e) => setSearchParams({...searchParams, status: e.target.value})}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="VERIFIED">Verified</MenuItem>
                  <MenuItem value="DELETED">Deleted</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth size="small" disabled={serviceNamesLoading}>
                <InputLabel>Service Name</InputLabel>
                <Select
                  value={searchParams.service_code}
                  label="Service Name"
                  onChange={(e) => setSearchParams({...searchParams, service_code: e.target.value})}
                  startAdornment={
                    serviceNamesLoading
                      ? <InputAdornment position="start"><CircularProgress size={14} /></InputAdornment>
                      : undefined
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  {serviceNames.map((sn) => (
                    <MenuItem key={sn.service_name_id} value={String(sn.service_code)}>
                      {sn.service_name}
                      <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                        ({sn.service_code})
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="From Date"
                type="date"
                value={searchParams.from_date}
                onChange={(e) => setSearchParams({...searchParams, from_date: e.target.value})}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="To Date"
                type="date"
                value={searchParams.to_date}
                onChange={(e) => setSearchParams({...searchParams, to_date: e.target.value})}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 12 }}>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                  disabled={loading}
                  fullWidth
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleReset}
                  disabled={loading}
                  fullWidth
                >
                  Reset
                </Button>
                {hasPermission('export_data') && (
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleExport}
                    disabled={loading}
                    fullWidth
                  >
                    Export
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Bulk Actions Bar */}
      {hasRole('DATA_MANAGER') && selectedRecords.length > 0 && (
        <Card sx={{ mb: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), borderRadius: 2 }}>
          <CardContent sx={{ py: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {selectedRecords.length} record(s) selected
              </Typography>
              <Button
                variant="contained"
                color="success"
                startIcon={<VerifiedIcon />}
                onClick={handleBulkVerify}
                disabled={loading}
              >
                Verify Selected
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Error Alert */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)} sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Success Alert */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Records Table Card */}
      <Card 
        sx={{ 
          boxShadow: theme.shadows[4],
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              <Box sx={{ mr: 3 }}>Records ({totalRecords})</Box>
            </Typography>
            {loading && <LinearProgress sx={{ flex: 1, mx: 2, maxWidth: 300 }} />}
          </Box>

          <TableContainer>
            <Table 
            size="small"
            >
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                  {hasRole('DATA_MANAGER') && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectAll}
                        onChange={handleSelectAll}
                        indeterminate={selectedRecords.length > 0 && !selectAll}
                      />
                    </TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 700, width: 70 }}>Sl. No.</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Passport No</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Mobile</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Enrolment No</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Amount (JD)</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Created By</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Entry Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={hasRole('DATA_MANAGER') ? 11 : 10} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        {loading ? 'Loading records...' : 'No records found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  records.map((record, index) => (
                    <TableRow 
                      key={record.passport_applicants_id}
                      hover
                      sx={{ 
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) },
                        bgcolor: selectedRecords.includes(record.passport_applicants_id) 
                          ? alpha(theme.palette.primary.main, 0.05) 
                          : 'transparent'
                      }}
                    >
                      {hasRole('DATA_MANAGER') && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedRecords.includes(record.passport_applicants_id)}
                            onChange={() => handleSelectRecord(record.passport_applicants_id)}
                            disabled={record.current_status !== 'PENDING'}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          {getSerialNumber(index)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {record.applicants_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={record.applicants_type_display} 
                          size="small"
                          color={record.applicants_type === 'INDIVIDUAL' ? 'primary' : 'secondary'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{record.passport_no}</TableCell>
                      <TableCell>{record.mobile_number}</TableCell>
                      <TableCell>{record.enrolment_no}</TableCell>
                      <TableCell>
                        {record.amount_jd ? (
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                            {Number(record.amount_jd).toFixed(2)}
                          </Typography>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <StatusChip status={record.current_status} />
                      </TableCell>
                      <TableCell>{record.created_by}</TableCell>
                      <TableCell sx={{ px: 1 }}>{formatDate(record.date_of_entry)}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleView(record)}
                              sx={{ color: 'info.main' }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          {canEdit(record) && (
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(record)}
                                sx={{ color: 'warning.main' }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          {canDelete(record) && (
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(record)}
                                sx={{ color: 'error.main' }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          {canVerify(record) && (
                            <Tooltip title="Verify">
                              <IconButton
                                size="small"
                                onClick={() => handleVerify(record)}
                                sx={{ color: 'success.main' }}
                              >
                                <VerifiedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', boxShadow: theme.shadows[8] } }}
      >
        <DialogTitle sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2, px: 3,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ bgcolor: alpha('#fff', 0.2), borderRadius: 1.5, p: 0.6, display: 'flex' }}>
              <ViewIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>Record Details</Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>{selectedRecord?.applicants_name}</Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setViewDialogOpen(false)} sx={{ color: 'white', '&:hover': { bgcolor: alpha('#fff', 0.15) } }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {selectedRecord && (
            <>
              {/* Status ribbon */}
              <Box sx={{
                px: 3, py: 1.5,
                bgcolor: alpha(theme.palette.grey[100], 1),
                borderBottom: `1px solid ${theme.palette.divider}`,
                display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
              }}>
                <StatusChip status={selectedRecord.current_status} />
                <Chip label={selectedRecord.applicants_type_display} size="small"
                  color={selectedRecord.applicants_type === 'INDIVIDUAL' ? 'primary' : 'secondary'}
                  variant="outlined" sx={{ fontWeight: 600 }} />
                <Chip label={selectedRecord.passport_type_display} size="small" variant="outlined" sx={{ fontWeight: 500 }} />
                {selectedRecord.service_code && (() => {
                  const sn = serviceNames.find(s => s.service_code === selectedRecord.service_code);
                  return sn ? (
                    <Chip icon={<ServiceIcon sx={{ fontSize: 14 }} />} label={sn.service_name} size="small" variant="outlined" color="info" sx={{ fontWeight: 500 }} />
                  ) : null;
                })()}
              </Box>

              <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {(() => {
                  const SectionLabel = ({ title }: { title: string }) => (
                    <Typography variant="subtitle2" sx={{
                      fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6,
                      color: 'primary.main', borderBottom: `2px solid ${theme.palette.primary.main}`,
                      pb: 0.6, mb: 1.5,
                    }}>{title}</Typography>
                  );

                  const Field = ({ label, value, bold }: { label: string; value: React.ReactNode; bold?: boolean }) => (
                    <Box sx={{
                      p: 1.5, borderRadius: 1.5,
                      bgcolor: alpha(theme.palette.grey[200], 0.45),
                      '&:hover': { bgcolor: alpha(theme.palette.grey[200], 0.7) },
                    }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>{label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: bold ? 700 : 500, mt: 0.2 }}>{value ?? '—'}</Typography>
                    </Box>
                  );

                  const HighlightField = ({ label, value }: { label: string; value: React.ReactNode }) => (
                    <Box sx={{
                      p: 1.5, borderRadius: 1.5,
                      bgcolor: alpha(theme.palette.success.main, 0.06),
                      border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
                    }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>{label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main', mt: 0.2 }}>{value ?? '—'}</Typography>
                    </Box>
                  );

                  // Iqama expiry warning
                  const iqamaExpiry = selectedRecord.expiry_date_of_iqama;
                  const daysLeft = selectedRecord.days_until_iqama_expiry;
                  const iqamaExpired  = daysLeft !== null && daysLeft !== undefined && daysLeft < 0;
                  const iqamaWarning  = daysLeft !== null && daysLeft !== undefined && daysLeft >= 0 && daysLeft <= 30;

                  // Service label
                  const serviceSn = serviceNames.find(s => s.service_code === selectedRecord.service_code);
                  const serviceLabel = serviceSn
                    ? `${serviceSn.service_name} (${serviceSn.service_code})`
                    : selectedRecord.service_code ? String(selectedRecord.service_code) : null;

                  return (
                    <>
                      {/* ── Personal Information ── */}
                      <Box>
                        <SectionLabel title="Personal Information" />
                        <Grid container spacing={1.5}>
                          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Field label="Applicant Name" value={selectedRecord.applicants_name} bold />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Field label="Applicant Type" value={selectedRecord.applicants_type_display} />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Field label="Mobile Number" value={selectedRecord.mobile_number} />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Field label="Employer Name"
                              value={selectedRecord.applicants_type === 'INDIVIDUAL' ? 'Self' : (selectedRecord.employer_name || null)} />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Field label="Place / Area" value={selectedRecord.place_area} />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Field label="Profession Name" value={selectedRecord.applicants_profession} />
                          </Grid>
                        </Grid>
                      </Box>

                      {/* ── Passport Details ── */}
                      <Box>
                        <SectionLabel title="Passport Details" />
                        <Grid container spacing={1.5}>
                          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Field label="Passport Number" value={selectedRecord.passport_no} bold />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Field label="Passport Type" value={selectedRecord.passport_type_display} />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Field label="Date of Entry" value={formatDate(selectedRecord.date_of_entry)} />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Field label="Enrolment Number" value={selectedRecord.enrolment_no} />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Field label="Service" value={serviceLabel} />
                          </Grid>
                        </Grid>
                      </Box>

                      {/* ── Iqama Details ── */}
                      <Box>
                        <SectionLabel title="Iqama Details" />
                        <Grid container spacing={1.5}>
                          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Field label="Iqama Number" value={selectedRecord.iqama_number} />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Box sx={{
                              p: 1.5, borderRadius: 1.5,
                              bgcolor: iqamaExpired
                                ? alpha(theme.palette.error.main, 0.07)
                                : iqamaWarning
                                  ? alpha(theme.palette.warning.main, 0.07)
                                  : alpha(theme.palette.grey[200], 0.45),
                              border: iqamaExpired
                                ? `1px solid ${alpha(theme.palette.error.main, 0.3)}`
                                : iqamaWarning
                                  ? `1px solid ${alpha(theme.palette.warning.main, 0.3)}`
                                  : 'none',
                            }}>
                              <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
                                Iqama Expiry Date
                              </Typography>
                              <Typography variant="body2" sx={{
                                fontWeight: 600, mt: 0.2,
                                color: iqamaExpired ? 'error.main' : iqamaWarning ? 'warning.main' : 'text.primary',
                              }}>
                                {iqamaExpiry ? formatDate(iqamaExpiry) : '—'}
                              </Typography>
                              {iqamaExpired && (
                                <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 700 }}>
                                  Expired {Math.abs(daysLeft!)} day(s) ago — Take full payment
                                </Typography>
                              )}
                              {iqamaWarning && (
                                <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 700 }}>
                                  Expires in {daysLeft} day(s)
                                </Typography>
                              )}
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>

                      {/* ── Financial Information ── */}
                      <Box>
                        <SectionLabel title="Financial Information" />
                        <Grid container spacing={1.5}>
                          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Field label="Money Receipt No" value={selectedRecord.money_receipt_no} />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <HighlightField
                              label="Amount (JD)"
                              value={selectedRecord.amount_jd ? Number(selectedRecord.amount_jd).toFixed(2) : null}
                            />
                          </Grid>
                        </Grid>
                      </Box>

                      {/* ── Remarks ── */}
                      {selectedRecord.remarks && (
                        <Box sx={{
                          p: 2, borderRadius: 1.5,
                          bgcolor: alpha(theme.palette.info.main, 0.06),
                          border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                        }}>
                          <Typography variant="caption" sx={{ color: 'info.main', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                            Remarks
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>{selectedRecord.remarks}</Typography>
                        </Box>
                      )}

                      {/* ── Audit Trail ── */}
                      <Box sx={{
                        p: 2, borderRadius: 2,
                        bgcolor: alpha(theme.palette.grey[800], 0.04),
                        border: `1px dashed ${theme.palette.divider}`,
                      }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Audit Trail
                        </Typography>
                        <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="text.secondary">Created By</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedRecord.created_by || '—'}</Typography>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="text.secondary">Created At</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {formatDateTime(selectedRecord.created_at)}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="text.secondary">Updated By</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedRecord.updated_by || '—'}</Typography>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="text.secondary">Updated At</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {formatDateTime(selectedRecord.updated_at)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </>
                  );
                })()}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${theme.palette.divider}`, px: 3, py: 1.5 }}>
          <Button onClick={() => setViewDialogOpen(false)} variant="outlined">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'warning.main', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EditIcon sx={{ mr: 1 }} />
            Edit Record
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2} sx={{ py: 1 }}>
            {/* Service Type */}
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth required error={!!editErrors.service_code} disabled={serviceNamesLoading}>
                <InputLabel>Service Type</InputLabel>
                <Select
                  value={editFormData.service_code ?? ''}
                  label="Service Type"
                  onChange={(e) => handleEditChange('service_code', e.target.value)}
                >
                  <MenuItem value="" disabled><em>Select a service…</em></MenuItem>
                  {serviceNames.map((sn) => (
                    <MenuItem key={sn.service_name_id} value={sn.service_code}>
                      {sn.service_name}
                      <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                        ({sn.service_code})
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
                {editErrors.service_code && (
                  <Typography variant="caption" color="error">{editErrors.service_code}</Typography>
                )}
              </FormControl>
            </Grid>
            {/* Applicant Type */}
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth required error={!!editErrors.applicants_type}>
                <InputLabel>Applicant Type</InputLabel>
                <Select
                  value={editFormData.applicants_type || ''}
                  label="Applicant Type"
                  onChange={(e) => handleEditChange('applicants_type', e.target.value)}
                >
                  <MenuItem value="INDIVIDUAL">Individual</MenuItem>
                  <MenuItem value="ORGANIZATION">Organization</MenuItem>
                </Select>
                {editErrors.applicants_type && (
                  <Typography variant="caption" color="error">{editErrors.applicants_type}</Typography>
                )}
              </FormControl>
            </Grid>
            {/* Employer Name */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Employer Name"
                value={editFormData.applicants_type === 'INDIVIDUAL' ? 'Self' : (editFormData.employer_name || '')}
                onChange={(e) => handleEditChange('employer_name', e.target.value)}
                required={editFormData.applicants_type === 'ORGANIZATION'}
                disabled={editFormData.applicants_type === 'INDIVIDUAL'}
                error={!!editErrors.employer_name}
                helperText={editErrors.employer_name || (editFormData.applicants_type === 'INDIVIDUAL' ? 'Automatically set to "Self" for individual applicants' : 'Required for organization applicants')}
                placeholder={editFormData.applicants_type === 'ORGANIZATION' ? 'Enter employer / org name' : ''}
              />
            </Grid>
            {/* Applicant Name */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Applicant Name"
                value={editFormData.applicants_name || ''}
                onChange={(e) => handleEditChange('applicants_name', e.target.value)}
                required
                error={!!editErrors.applicants_name}
                helperText={editErrors.applicants_name}
              />
            </Grid>
            {/* Mobile Number */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Mobile Number"
                value={editFormData.mobile_number || ''}
                onChange={(e) => handleEditChange('mobile_number', e.target.value)}
                required
                error={!!editErrors.mobile_number}
                helperText={editErrors.mobile_number}
                placeholder="e.g. 0791234567"
              />
            </Grid>
            {/* Passport Number */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Passport Number"
                value={editFormData.passport_no || ''}
                onChange={(e) => handleEditChange('passport_no', e.target.value)}
                required
                error={!!editErrors.passport_no}
                helperText={editErrors.passport_no}
              />
            </Grid>
            {/* Passport Type */}
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth required error={!!editErrors.passport_type}>
                <InputLabel>Passport Type</InputLabel>
                <Select
                  value={editFormData.passport_type || ''}
                  label="Passport Type"
                  onChange={(e) => handleEditChange('passport_type', e.target.value)}
                >
                  <MenuItem value="MRP">Machine Readable Passport (MRP)</MenuItem>
                  <MenuItem value="EPP">Electronic Passport (EPP)</MenuItem>
                </Select>
                {editErrors.passport_type && (
                  <Typography variant="caption" color="error">{editErrors.passport_type}</Typography>
                )}
              </FormControl>
            </Grid>
            {/* Date of Entry */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Date of Entry"
                type="date"
                value={editFormData.date_of_entry || ''}
                onChange={(e) => handleEditChange('date_of_entry', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                error={!!editErrors.date_of_entry}
                helperText={editErrors.date_of_entry}
              />
            </Grid>
            {/* Place / Area */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Place / Area"
                value={editFormData.place_area || ''}
                onChange={(e) => handleEditChange('place_area', e.target.value)}
                required
                error={!!editErrors.place_area}
                helperText={editErrors.place_area}
              />
            </Grid>
            {/* Applicant's Profession */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Profession Name"
                value={editFormData.applicants_profession || ''}
                onChange={(e) => handleEditChange('applicants_profession', e.target.value)}
                required
                error={!!editErrors.applicants_profession}
                helperText={editErrors.applicants_profession}
              />
            </Grid>
            {/* Iqama Number */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Iqama Number"
                value={editFormData.iqama_number || ''}
                onChange={(e) => handleEditChange('iqama_number', e.target.value)}
                helperText="If provided, expiry date is required"
              />
            </Grid>
            {/* Iqama Expiry Date */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Iqama Expiry Date"
                type="date"
                value={editFormData.expiry_date_of_iqama || ''}
                onChange={(e) =>
                  handleEditChange('expiry_date_of_iqama', e.target.value)
                }
                InputLabelProps={{ shrink: true }}
                disabled={
                  !editFormData.iqama_number ||
                  editFormData.iqama_number.trim() === ''
                }
                error={!!editErrors.expiry_date_of_iqama}
                helperText={
                  editErrors.expiry_date_of_iqama ? (
                    editErrors.expiry_date_of_iqama
                  ) : isEditIqamaDatePast ? (<span style={{ color: '#ed6c02', fontWeight: 700 }}>Take full payment</span>) : null
                }
              />
            </Grid>
            {/* Enrolment Number */}
            <Grid size={{ xs: 12 }}>
              <Tooltip
                title={
                  editFormData.passport_type === 'MRP'
                    ? 'Auto-generated from the current date & time for MRP passports. This field cannot be edited.'
                    : ''
                }
                placement="top"
                arrow
              >
                <TextField
                  fullWidth
                  label="Enrolment Number"
                  value={editFormData.enrolment_no || ''}
                  onChange={(e) => handleEditChange('enrolment_no', e.target.value)}
                  required
                  placeholder={
                    editFormData.passport_type === 'MRP'
                      ? 'Auto-generated (MRPDDMMYYYYHHmmss)'
                      : 'Enter Enrolment Number'
                  }
                  error={!!editErrors.enrolment_no}
                  helperText={
                    editErrors.enrolment_no ||
                    (editFormData.passport_type === 'MRP'
                      ? '\uD83D\uDD12 Auto-generated for MRP — not editable'
                      : undefined)
                  }
                  InputProps={{
                    readOnly: editFormData.passport_type === 'MRP',
                  }}
                  sx={
                    editFormData.passport_type === 'MRP'
                      ? {
                          '& .MuiInputBase-root': {
                            bgcolor: (t) => t.palette.action.disabledBackground,
                            cursor: 'not-allowed',
                          },
                        }
                      : {}
                  }
                />
              </Tooltip>
            </Grid>
            {/* Money Receipt Number */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Money Receipt Number"
                value={editFormData.money_receipt_no || ''}
                onChange={(e) => handleEditChange('money_receipt_no', e.target.value)}
              />
            </Grid>
            {/* Amount (JD) */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Amount (JD)"
                type="number"
                value={editFormData.amount_jd ?? ''}
                onWheel={(e) => (e.target as HTMLElement).blur()}
                sx={{
                  '& input[type=number]': { MozAppearance: 'textfield' },
                  '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
                  '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                }}
                onChange={(e) => handleEditChange('amount_jd', e.target.value === '' ? null : parseFloat(e.target.value))}
                inputProps={{ 
                  startAdornment: (
                    <InputAdornment position="start">
                      <MoneyIcon color="action" />
                    </InputAdornment>
                  ),
                  inputProps: { step: 0.01, min: 0 },
                }}
                error={!!editErrors.amount_jd}
                helperText={editErrors.amount_jd}
              />
            </Grid>
            {/* Remarks */}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Remarks"
                multiline
                rows={3}
                value={editFormData.remarks || ''}
                onChange={(e) => handleEditChange('remarks', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={loading}>Cancel</Button>
          <Button 
            onClick={handleUpdateRecord} 
            variant="contained" 
            color="warning"
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update Record'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ bgcolor: 'error.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.8, px: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ bgcolor: alpha('#fff', 0.2), borderRadius: 1, p: 0.5, display: 'flex' }}>
              <WarningIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Confirm Deletion</Typography>
          </Box>
          <IconButton onClick={() => setDeleteDialogOpen(false)} sx={{ color: 'white', p: 0.5, '&:hover': { bgcolor: alpha('#fff', 0.15) } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2.5, pb: 1, px: 2.5 }}>
          <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>
            This action <strong>cannot be undone</strong>. The record will be permanently marked as deleted.
          </Alert>

          {selectedRecord && (
            <Box sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.error.main, 0.25)}`, overflow: 'hidden' }}>
              {/* mini header */}
              <Box sx={{ bgcolor: alpha(theme.palette.error.main, 0.08), px: 2, py: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'error.main', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Record being deleted
                </Typography>
              </Box>

              <Grid container sx={{ p: 2 }} spacing={1.5}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Service</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {selectedRecord.service_code
                      ? (() => {
                          const sn = serviceNames.find(s => s.service_code === selectedRecord.service_code);
                          return sn ? `${sn.service_name} (${sn.service_code})` : String(selectedRecord.service_code);
                        })()
                      : '—'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Applicant Type</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedRecord.applicants_type_display}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Employer</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedRecord.applicants_type === 'INDIVIDUAL' ? 'Self' : (selectedRecord.employer_name || '—')}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Name</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{selectedRecord.applicants_name}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Mobile</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedRecord.mobile_number}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Passport No</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{selectedRecord.passport_no}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Passport Type</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedRecord.passport_type_display}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Date of Entry</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDate(selectedRecord.date_of_entry)}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Place / Area</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedRecord.place_area || '—'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Profession</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedRecord.applicants_profession || '—'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Iqama No</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedRecord.iqama_number || '—'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Iqama Expiry Date</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedRecord.expiry_date_of_iqama ? formatDate(selectedRecord.expiry_date_of_iqama) : '—'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Enrolment No</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedRecord.enrolment_no}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Money Receipt No</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedRecord.money_receipt_no || '—'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Amount (JD)</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                    {selectedRecord.amount_jd ? Number(selectedRecord.amount_jd).toFixed(2) : '—'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Status</Typography>
                  <Box sx={{ mt: 0.3 }}><StatusChip status={selectedRecord.current_status} /></Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Created By</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedRecord.created_by || '—'}</Typography>
                </Grid>
                {selectedRecord.remarks && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Remarks</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontStyle: 'italic' }}>{selectedRecord.remarks}</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 1, gap: 1 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={loading} variant="outlined" sx={{ flex: 1 }}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={loading}
            startIcon={<DeleteIcon />}
            sx={{ flex: 1 }}
          >
            {loading ? 'Deleting...' : 'Delete Record'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Verify Single Dialog */}
      <Dialog
        open={verifyDialogOpen}
        onClose={() => setVerifyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ bgcolor: 'success.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.8, px: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ bgcolor: alpha('#fff', 0.2), borderRadius: 1, p: 0.5, display: 'flex' }}>
              <CheckCircleIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Verify Record</Typography>
          </Box>
          <IconButton onClick={() => setVerifyDialogOpen(false)} sx={{ color: 'white', p: 0.5, '&:hover': { bgcolor: alpha('#fff', 0.15) } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2.5, pb: 1, px: 2.5 }}>
          <Alert severity="info" sx={{ mb: 2, borderRadius: 1.5 }}>
            Once verified, this record <strong>cannot be edited or deleted</strong> by anyone.
          </Alert>

          {selectedRecord && (
            <Box sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`, overflow: 'hidden', mb: 2 }}>
              {/* mini header */}
              <Box sx={{ bgcolor: alpha(theme.palette.success.main, 0.08), px: 2, py: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'success.main', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Record being verified
                </Typography>
              </Box>

              <Grid container sx={{ p: 2 }} spacing={1.5}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Service</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {selectedRecord.service_code
                      ? (() => {
                          const sn = serviceNames.find(s => s.service_code === selectedRecord.service_code);
                          return sn ? `${sn.service_name} (${sn.service_code})` : String(selectedRecord.service_code);
                        })()
                      : '—'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Applicant Type</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedRecord.applicants_type_display}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Employer</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedRecord.applicants_type === 'INDIVIDUAL' ? 'Self' : (selectedRecord.employer_name || '—')}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Name</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{selectedRecord.applicants_name}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Mobile</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedRecord.mobile_number}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Passport No</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{selectedRecord.passport_no}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Passport Type</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedRecord.passport_type_display}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Date of Entry</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDate(selectedRecord.date_of_entry)}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Place / Area</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedRecord.place_area || '—'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Profession</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedRecord.applicants_profession || '—'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Iqama No</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedRecord.iqama_number || '—'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Iqama Expiry Date</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedRecord.expiry_date_of_iqama ? formatDate(selectedRecord.expiry_date_of_iqama) : '—'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Enrolment No</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedRecord.enrolment_no}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Money Receipt No</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedRecord.money_receipt_no || '—'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Amount (JD)</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                    {selectedRecord.amount_jd ? Number(selectedRecord.amount_jd).toFixed(2) : '—'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Status</Typography>
                  <Box sx={{ mt: 0.3 }}><StatusChip status={selectedRecord.current_status} /></Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Created By</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedRecord.created_by || '—'}</Typography>
                </Grid>
                {selectedRecord.remarks && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Remarks</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontStyle: 'italic' }}>{selectedRecord.remarks}</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          <TextField
            fullWidth
            label="Verification Remarks"
            multiline
            rows={3}
            value={verifyRemarks}
            onChange={(e) => setVerifyRemarks(e.target.value)}
            placeholder="Enter remarks for verification..."
          />
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 1, gap: 1 }}>
          <Button onClick={() => setVerifyDialogOpen(false)} disabled={loading} variant="outlined" sx={{ flex: 1 }}>Cancel</Button>
          <Button
            onClick={handleConfirmVerify}
            color="success"
            variant="contained"
            disabled={loading}
            startIcon={<VerifiedIcon />}
            sx={{ flex: 1 }}
          >
            {loading ? 'Verifying...' : 'Verify Record'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Verify Dialog */}
      <Dialog 
        open={bulkVerifyDialogOpen} 
        onClose={() => setBulkVerifyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'success.main', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircleIcon sx={{ mr: 1 }} />
            Bulk Verify Records
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            You are about to verify {selectedRecords.length} record(s). Once verified, these records cannot be edited or deleted.
          </Alert>
          <TextField
            fullWidth
            label="Verification Remarks"
            multiline
            rows={3}
            value={verifyRemarks}
            onChange={(e) => setVerifyRemarks(e.target.value)}
            placeholder="Enter remarks for bulk verification..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkVerifyDialogOpen(false)} disabled={loading}>Cancel</Button>
          <Button 
            onClick={handleConfirmBulkVerify} 
            color="success" 
            variant="contained"
            disabled={loading}
            startIcon={<VerifiedIcon />}
          >
            {loading ? 'Verifying...' : `Verify ${selectedRecords.length} Record(s)`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageRecords;